import { useMemo } from 'react';
import { useGetPublicListValuesQuery } from '@/services/ListValueService';
import { ManagedListType } from '@/types/list-value.types';
import { useLanguage } from '@/i18n';

export interface ManagedListOption {
  id: string;
  value: string;
  label: string;
  icon_id?: number | null;
}

export const formatManagedListKey = (value?: string | null) => {
  const normalized = String(value || '').trim();
  if (!normalized) return '';

  return normalized
    .split(/[_-]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const useManagedListOptions = (type: ManagedListType) => {
  const { currentLanguage } = useLanguage();
  const query = useGetPublicListValuesQuery({
    type,
    language: currentLanguage.code,
  });

  const values = useMemo(() => query.data || [], [query.data]);

  const options = useMemo<ManagedListOption[]>(
    () =>
      values.map((item) => ({
        id: String(item.id),
        value: item.key,
        label: item.label || formatManagedListKey(item.key),
        icon_id: item.icon_id ?? null,
      })),
    [values]
  );

  const keys = useMemo(() => options.map((option) => option.value), [options]);

  const labelByKey = useMemo(
    () =>
      new Map(options.map((option) => [option.value, option.label] as const)),
    [options]
  );

  const getLabel = (value?: string | null) => {
    const normalized = String(value || '').trim();
    return labelByKey.get(normalized) || formatManagedListKey(normalized);
  };

  return {
    ...query,
    values,
    options,
    keys,
    labelByKey,
    getLabel,
  };
};
